import * as React from 'react'
import type { EvidenceItem } from '@atbash-labs/ontology-core'
import {
  usePlantSessionPaint,
  type IngestSessionEvidence,
  type PlantProvenance
} from '@axilon-apps/domains-ontology-ui-state'
import { SourceDocChip, type SourceDocRef } from '../../../components/SourceDocViewer'
import {
  SessionCompactNewBadge,
  SessionCompactUpdatedBadge,
  sessionMarkClass
} from '../session-mark-ui'
import { ConfidenceValue } from './ConfidenceValue'

function formatSource(file: string, page?: number): string {
  return page !== undefined ? `${file} · p. ${page}` : file
}

export function provenanceSourceLabel(provenance: PlantProvenance | undefined): string | null {
  return provenance?.source_file
    ? formatSource(provenance.source_file, provenance.source_page)
    : null
}

function formatTimestamp(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

/**
 * The top-level provenance fields are a rollup of the newest evidence
 * item, so a single-evidence entity would otherwise render the same
 * file / page / excerpt twice. Skip the list when it adds nothing.
 */
function isRollupDuplicate(items: EvidenceItem[], provenance: PlantProvenance): boolean {
  if (items.length !== 1) return false
  const [item] = items
  return (
    item.source_file === provenance.source_file &&
    item.source_page === provenance.source_page &&
    (item.source_excerpt ?? '') === (provenance.source_excerpt ?? '')
  )
}

function citationKey(sourceFile: string, sourcePage?: number, sourceExcerpt?: string): string {
  return `${sourceFile}\0${sourcePage ?? ''}\0${sourceExcerpt ?? ''}`
}

function basenameMatch(left: string, right: string): boolean {
  const normalize = (value: string) => value.replaceAll('\\', '/').split('/').pop() ?? value
  return left === right || normalize(left) === normalize(right)
}

function citationMatchesProvenance(
  citation: IngestSessionEvidence,
  provenance: PlantProvenance
): boolean {
  if (!provenance.source_file) return false
  return (
    basenameMatch(citation.sourceFile, provenance.source_file) &&
    citation.sourcePage === provenance.source_page &&
    (citation.sourceExcerpt ?? '') === (provenance.source_excerpt ?? '')
  )
}

function citationMatchesEvidenceItem(citation: IngestSessionEvidence, item: EvidenceItem): boolean {
  return (
    basenameMatch(citation.sourceFile, item.source_file) &&
    citation.sourcePage === item.source_page &&
    (citation.sourceExcerpt ?? '') === (item.source_excerpt ?? '')
  )
}

function useSessionSourceEvidence(entityId: string | undefined): {
  citations: IngestSessionEvidence[]
  mark: 'added' | 'touched' | undefined
} {
  const { active, sourceEvidence, markFor } = usePlantSessionPaint()
  if (!entityId || !active) return { citations: [], mark: undefined }
  const mark = markFor(entityId)
  if (mark === undefined || mark === 'dimmed' || mark === 'deleted') {
    return { citations: [], mark: undefined }
  }
  const citations = sourceEvidence.get(entityId) ?? []
  if (citations.length === 0) return { citations: [], mark: undefined }
  return { citations, mark: mark === 'added' ? 'added' : 'touched' }
}

/**
 * Page-level Source and evidence section — always visible when provenance
 * or session-local citations are present. Session citations tint the card
 * with the existing paint highlight (green new / yellow updated).
 */
export function EntityProvenancePanel({
  entityId,
  provenance,
  jobId,
  onOpenSource
}: {
  entityId?: string
  provenance: PlantProvenance | undefined
  jobId?: string
  onOpenSource: (args: SourceDocRef) => void
}): React.ReactElement | null {
  const { citations: sessionCitations, mark: sessionMark } = useSessionSourceEvidence(entityId)
  if (!provenance && sessionCitations.length === 0) return null

  const sourceLabel = provenanceSourceLabel(provenance)
  const rollupSourceJobId =
    provenance !== undefined
      ? sessionCitations.find((citation) => citationMatchesProvenance(citation, provenance))
          ?.sourceJobId
      : undefined
  const openJobId = rollupSourceJobId ?? jobId
  const sourceChip =
    openJobId && provenance?.source_file ? (
      <SourceDocChip
        jobId={openJobId}
        sourceFile={provenance.source_file}
        sourcePage={provenance.source_page}
        sourceExcerpt={provenance.source_excerpt}
        onOpen={onOpenSource}
      />
    ) : null
  const extractedBy = [provenance?.extraction_model, provenance?.extraction_method]
    .filter(Boolean)
    .join(' · ')
  const evidenceItems =
    provenance?.evidence_items &&
    provenance &&
    !isRollupDuplicate(provenance.evidence_items, provenance)
      ? provenance.evidence_items
      : []

  const coveredKeys = new Set<string>()
  if (provenance?.source_file) {
    coveredKeys.add(
      citationKey(provenance.source_file, provenance.source_page, provenance.source_excerpt)
    )
  }
  for (const item of evidenceItems) {
    coveredKeys.add(citationKey(item.source_file, item.source_page, item.source_excerpt))
  }
  const unmatchedSession = sessionCitations.filter((citation) => {
    if (provenance && citationMatchesProvenance(citation, provenance)) return false
    if (evidenceItems.some((item) => citationMatchesEvidenceItem(citation, item))) return false
    return !coveredKeys.has(
      citationKey(citation.sourceFile, citation.sourcePage, citation.sourceExcerpt)
    )
  })

  const fields: Array<[string, React.ReactNode]> = []
  let sessionOnly = unmatchedSession
  if (sourceChip) {
    fields.push(['Source', sourceChip])
  } else if (sourceLabel) {
    fields.push([
      'Source',
      <span key="source" className="font-mono text-12 text-ink">
        {sourceLabel}
      </span>
    ])
  } else if (!provenance && unmatchedSession.length > 0) {
    const [first, ...rest] = unmatchedSession
    fields.push([
      'Source',
      <SourceDocChip
        key="session-source"
        jobId={first!.sourceJobId}
        sourceFile={first!.sourceFile}
        sourcePage={first!.sourcePage}
        sourceExcerpt={first!.sourceExcerpt}
        onOpen={onOpenSource}
      />
    ])
    sessionOnly = rest
  }
  if (provenance?.source_kind) fields.push(['Kind', provenance.source_kind])
  else if (sessionCitations[0]?.sourceKind) {
    fields.push(['Kind', sessionCitations[0].sourceKind])
  }
  if (extractedBy) fields.push(['Extracted by', extractedBy])
  if (provenance?.last_evidence_at) {
    fields.push(['Last evidence', formatTimestamp(provenance.last_evidence_at)])
  }

  const listCount = evidenceItems.length + sessionOnly.length
  if (
    fields.length === 0 &&
    !provenance?.source_excerpt &&
    evidenceItems.length === 0 &&
    sessionCitations.length === 0
  ) {
    return null
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          Source and evidence
        </h3>
        {listCount > 0 ? <span className="text-xs text-ink-3">{listCount}</span> : null}
        {sessionMark === 'added' ? <SessionCompactNewBadge /> : null}
        {sessionMark === 'touched' ? <SessionCompactUpdatedBadge /> : null}
      </div>
      <div
        className={sessionMarkClass(
          'rounded-xl border bg-card p-4 text-card-foreground shadow-sm',
          sessionMark
        )}
      >
        {fields.length > 0 && (
          <div className="grid min-w-0 grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]">
            {fields.map(([key, value]) => (
              <div key={key} className="min-w-0">
                <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
                  {key}
                </div>
                <div className="mt-0.5 min-w-0 break-words text-12 text-ink-2">{value}</div>
              </div>
            ))}
          </div>
        )}

        {provenance?.source_excerpt && (
          <blockquote className="mb-0 mt-3 border-l-2 border-brand/40 pl-2.5 text-12 italic text-ink-3">
            {provenance.source_excerpt}
          </blockquote>
        )}

        {(evidenceItems.length > 0 || sessionOnly.length > 0) && (
          <div className="mt-3 divide-y rounded-md border bg-bg-elev">
            {evidenceItems.map((item, index) => {
              const itemJobId =
                sessionCitations.find((citation) => citationMatchesEvidenceItem(citation, item))
                  ?.sourceJobId ?? jobId
              return (
                <div key={`${item.source_file}-${index}`} className="px-2.5 py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate font-mono text-11 text-ink">
                      {formatSource(item.source_file, item.source_page)}
                    </span>
                    <SourceDocChip
                      compact
                      jobId={itemJobId}
                      sourceFile={item.source_file}
                      sourcePage={item.source_page}
                      sourceExcerpt={item.source_excerpt}
                      onOpen={onOpenSource}
                    />
                    {item.extracted_at && (
                      <span className="hidden text-10 text-ink-4 sm:inline">
                        {formatTimestamp(item.extracted_at)}
                      </span>
                    )}
                    <ConfidenceValue value={item.confidence} />
                  </div>
                  {item.source_excerpt && (
                    <p className="mb-0 mt-0.5 text-11 text-ink-3">{item.source_excerpt}</p>
                  )}
                </div>
              )
            })}
            {sessionOnly.map((citation) => (
              <div
                key={`${citation.sourceJobId}:${citation.sourceFile}:${citation.sourcePage ?? ''}:${citation.sourceExcerpt ?? ''}`}
                className="px-2.5 py-1.5"
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate font-mono text-11 text-ink">
                    {formatSource(citation.sourceFile, citation.sourcePage)}
                  </span>
                  <SourceDocChip
                    compact
                    jobId={citation.sourceJobId}
                    sourceFile={citation.sourceFile}
                    sourcePage={citation.sourcePage}
                    sourceExcerpt={citation.sourceExcerpt}
                    onOpen={onOpenSource}
                  />
                  {citation.confidence !== undefined ? (
                    <ConfidenceValue value={citation.confidence} />
                  ) : null}
                </div>
                {citation.sourceExcerpt ? (
                  <p className="mb-0 mt-0.5 text-11 text-ink-3">{citation.sourceExcerpt}</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
